import { AxiosError } from "axios";
import { createRoot } from "react-dom/client";
import React from "react";
import ApiErrorFallback from "../components/Common/ApiErrorFallback";

/**
 * Handles API errors with appropriate fallback UI for 5xx errors (except 500)
 * @param error The error object from axios
 * @param resetCallback Function to call when user wants to retry
 * @returns Whether the error was handled with a fallback UI
 */
export const handleApiError = (
  error: AxiosError,
  resetCallback: () => void = () => window.location.reload()
): boolean => {
  // Check if it's an API error with status
  if (error.response) {
    const status = error.response.status;

    // Handle 5xx errors except 500
    if (status >= 501 && status <= 599) {
      // Create container for error UI if it doesn't exist
      let errorContainer = document.getElementById("api-error-container");
      if (!errorContainer) {
        errorContainer = document.createElement("div");
        errorContainer.id = "api-error-container";
        errorContainer.style.position = "fixed";
        errorContainer.style.top = "0";
        errorContainer.style.left = "0";
        errorContainer.style.width = "100%";
        errorContainer.style.height = "100%";
        errorContainer.style.zIndex = "9999";
        document.body.appendChild(errorContainer);
      }

      // Render error component
      const root = createRoot(errorContainer);
      root.render(
        React.createElement(ApiErrorFallback, {
          errorCode: status,
          resetError: () => {
            // Remove error UI
            if (errorContainer && errorContainer.parentNode) {
              errorContainer.parentNode.removeChild(errorContainer);
            }
            // Call reset callback
            resetCallback();
          },
        })
      );

      return true;
    }
  }

  // Error wasn't handled with fallback UI
  return false;
};

/**
 * Wraps an API call with error handling
 * @param apiCall The API call function to execute
 * @param resetCallback Function to call when user wants to retry
 * @returns The result of the API call
 */
export const withErrorHandling = async <T>(
  apiCall: () => Promise<T>,
  resetCallback?: () => void
): Promise<T> => {
  try {
    return await apiCall();
  } catch (error) {
    if (error instanceof Error) {
      const axiosError = error as AxiosError;
      const handled = handleApiError(axiosError, resetCallback);

      // If error was handled with UI, throw a custom error
      if (handled) {
        throw new Error("API error handled with fallback UI");
      }
    }

    // Re-throw original error if not handled
    throw error;
  }
};

export default {
  handleApiError,
  withErrorHandling,
};
